import React, { createContext, useContext, useState, useCallback } from "react";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { AlertTriangle, Trash2, RefreshCw } from "lucide-react";

const ConfirmCtx = createContext(null);

const VARIANTS = {
  delete: {
    icon: Trash2,
    iconBg: "bg-rose-50 text-rose-600",
    title: "Conferma eliminazione",
    message: "L'eliminazione è definitiva.\nSei sicuro?",
    confirmLabel: "SI, elimina",
    cancelLabel: "NO, annulla",
    confirmClass: "bg-rose-600 hover:bg-rose-700 focus:ring-rose-600",
  },
  replace: {
    icon: RefreshCw,
    iconBg: "bg-indigo-50 text-indigo-600",
    title: "Conferma sostituzione",
    message: "Il file precedente sarà sostituito definitivamente.\nSei sicuro?",
    confirmLabel: "SI, sostituisci",
    cancelLabel: "NO, annulla",
    confirmClass: "bg-indigo-600 hover:bg-indigo-700 focus:ring-indigo-600",
  },
  approve: {
    icon: AlertTriangle,
    iconBg: "bg-amber-50 text-amber-600",
    title: "Conferma approvazione",
    message: "L'operazione sarà eseguita immediatamente e non può essere annullata.\nSei sicuro?",
    confirmLabel: "SI, approva",
    cancelLabel: "NO, annulla",
    confirmClass: "bg-rose-600 hover:bg-rose-700 focus:ring-rose-600",
  },
};

export function ConfirmProvider({ children }) {
  const [state, setState] = useState(null);
  const [resolver, setResolver] = useState(null);

  const confirm = useCallback((opts = {}) => {
    return new Promise((resolve) => {
      const variant = VARIANTS[opts.variant || "delete"] || VARIANTS.delete;
      setState({
        variant: opts.variant || "delete",
        title: opts.title || variant.title,
        message: opts.message || variant.message,
        detail: opts.detail,
        confirmLabel: opts.confirmLabel || variant.confirmLabel,
        cancelLabel: opts.cancelLabel || variant.cancelLabel,
      });
      setResolver(() => resolve);
    });
  }, []);

  const handle = (result) => {
    if (resolver) resolver(result);
    setState(null);
    setResolver(null);
  };

  const v = state ? VARIANTS[state.variant] || VARIANTS.delete : null;
  const Icon = v?.icon;

  return (
    <ConfirmCtx.Provider value={confirm}>
      {children}
      <AlertDialog open={!!state} onOpenChange={(o) => !o && handle(false)}>
        <AlertDialogContent className="max-w-md">
          <AlertDialogHeader>
            <div className="flex items-center gap-3">
              {Icon && (
                <div className={`h-10 w-10 rounded-full ${v.iconBg} flex items-center justify-center shrink-0`}>
                  <Icon className="h-5 w-5" />
                </div>
              )}
              <AlertDialogTitle className="font-heading">{state?.title}</AlertDialogTitle>
            </div>
            <AlertDialogDescription className="whitespace-pre-line pt-2 text-slate-700 text-[15px]">
              {state?.message}
            </AlertDialogDescription>
            {state?.detail && (
              <div className="mt-1 rounded-md bg-slate-50 border border-slate-200 px-3 py-2 text-sm text-slate-700">
                {state.detail}
              </div>
            )}
          </AlertDialogHeader>
          <AlertDialogFooter className="gap-2">
            <AlertDialogCancel onClick={() => handle(false)} data-testid="confirm-cancel">
              {state?.cancelLabel}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={() => handle(true)}
              className={`${v?.confirmClass} text-white`}
              data-testid="confirm-ok"
            >
              {state?.confirmLabel}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </ConfirmCtx.Provider>
  );
}

export function useConfirm() {
  const ctx = useContext(ConfirmCtx);
  if (!ctx) {
    // Fallback: native confirm if provider missing
    return (opts) => Promise.resolve(window.confirm(opts?.message || "Sei sicuro?"));
  }
  return ctx;
}

/**
 * Double confirmation for destructive actions.
 * Shows the dialog twice — first with context, second with "SEI SICURO?" final warning.
 */
export function useDoubleConfirm() {
  const confirm = useConfirm();
  return async (opts = {}) => {
    const ok1 = await confirm(opts);
    if (!ok1) return false;
    const ok2 = await confirm({
      variant: opts.variant || "delete",
      title: "SEI SICURO?",
      message: opts.variant === "replace"
        ? "Ultima conferma prima della sostituzione definitiva."
        : "Ultima conferma prima dell'eliminazione definitiva.",
      detail: opts.detail,
      confirmLabel: opts.confirmLabel || (opts.variant === "replace" ? "SI, sostituisci" : "SI, elimina"),
      cancelLabel: opts.cancelLabel || "NO, annulla",
    });
    return ok2;
  };
}
